import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';
import type { Control, FieldValues, Path } from 'react-hook-form';
import type { UserRef } from '@app/shared';
import { api } from '../lib/api';

/**
 * A login ID typed or picked from suggestions (users the caller can see whose login ID starts
 * with what was typed, from the third character on). The field keeps the text; the server checks
 * that it names a user.
 */
export function LoginIdField({
  label,
  value,
  onChange,
  onBlur,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
}) {
  const text = value.trim();
  const suggestions = useQuery({
    queryKey: ['users', 'suggest', text.toLowerCase()],
    queryFn: () => api.get<{ data: UserRef[] }>('/users/suggest', { q: text }).then((r) => r.data),
    enabled: text.length >= 3,
    staleTime: 30_000,
  });
  return (
    <Autocomplete
      freeSolo
      options={(suggestions.data ?? []).map((u) => u.loginId)}
      getOptionLabel={(o) => o}
      filterOptions={(o) => o}
      inputValue={value}
      onInputChange={(_e, next) => onChange(next)}
      onChange={(_e, next) => onChange(next ?? '')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          onBlur={onBlur}
          error={Boolean(error)}
          helperText={error || 'Suggestions appear from the third letter'}
          slotProps={{ htmlInput: { ...params.inputProps, maxLength: 40 } }}
        />
      )}
    />
  );
}

/** `LoginIdField` for a React Hook Form form. */
export function LoginIdSuggest<T extends FieldValues>({
  control,
  name,
  label,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <LoginIdField
          label={label}
          value={(field.value as string | undefined) ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}
